// @flow
import * as React from 'react'
import type { BlogCategoryType } from '../../services/contentfulPosts'
import styled from 'styled-components'
import { colors } from '@components/shared/colors'
import { withRouter } from 'next/router'
import { Hidden } from 'react-awesome-styled-grid'
import { startCase } from 'lodash'
import { LinkWithUtms } from '../../actions/navigationActions'

type PropsType = {
  categories: BlogCategoryType[],
  router: {
    route: string,
    query: {
      slug?: string
    }
  }
}

const Arrow = (): React.Node => (<svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M1 1L7.48147 7.36469L13.9808 0.982421" stroke="white"/>
</svg>
)

function Categories ({ categories, router }: PropsType): React.Node {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  let activeCategory = null
  if (router.route.includes('cat')) {
    activeCategory = router.query?.slug || null
  }

  return (
    <>
      <Hidden xs={true} sm={true}>
        <CategoryContainer>
          {categories.map((cat: BlogCategoryType): React.Node => {
            const catName = cat.name.toLowerCase()
            const isActive = activeCategory ? activeCategory.toLowerCase() === catName : false
            return (
              <LinkWithUtms pathname={isActive ? '/blog' : `/blog/categories/${catName}`}
                key={cat.id}
              >
                <CategoryPill name={catName} isActive={isActive}>

                  <CategoryPillLink
                    name={catName}
                    isActive={isActive}
                  >
                    {cat.name}
                  </CategoryPillLink>

                  {isActive && (
                    <XButtonContainer>
                      <XButtonSvg size={8} name={catName}/>
                    </XButtonContainer>
                  )
                  }
                </CategoryPill>
              </LinkWithUtms>
            )
          })}
        </CategoryContainer>
      </Hidden>
      <Hidden sm={false} md={true} lg={true} xl={true}>
        <CategoryMobileContainer isOpen={isOpen} onClick={(): void => setIsOpen(!isOpen)}>
          <CategoryMobileActiveContainer>
            <CategoryMobileActiveText>{activeCategory ? startCase(activeCategory) : 'Categories'}</CategoryMobileActiveText>
            <CategoryMobileArrowImageContainer isOpen={isOpen}><Arrow/></CategoryMobileArrowImageContainer>
          </CategoryMobileActiveContainer>
          {isOpen && <CategoryMobileHr/>}
          {isOpen && (
            categories.map((cat: BlogCategoryType): React.Node => {
              const catName = cat.name.toLowerCase()
              const isActive = activeCategory ? activeCategory.toLowerCase() === catName : false
              return (

                <LinkWithUtms pathname={`/blog/categories/${catName}`}
                  key={cat.id}
                >

                  <CategoryMobilePill key={cat.id}>
                    <CategoryPill name={catName} isActive={isActive}>
                      <CategoryPillLink
                        isActive={isActive}
                        name={catName}
                        className={'pill-link'}
                      >
                        {cat.name}
                      </CategoryPillLink>
                    </CategoryPill>
                  </CategoryMobilePill>
                </LinkWithUtms>
              )
            })
          )}
          {(isOpen && activeCategory) && (
            <LinkWithUtms pathname={`/blog/`}>
              <CategoryMobileClearContainer>
                <CategoryMobileClearText>
                CLEAR
                </CategoryMobileClearText>
              </CategoryMobileClearContainer>
            </LinkWithUtms>
          )}
        </CategoryMobileContainer>
        <CategoryContainer>
        </CategoryContainer>
      </Hidden>
    </>
  )
}

const CategoryMobileContainer = styled.div`
  border-radius: 39px;
  border: 1px solid ${colors.sand};
  padding: 11px 24px 11px 24px;
  width: 90%;
  margin-top: 25px;
  margin-bottom: 40px;

 ${(props: { isOpen: boolean }): string => {
  return props.isOpen ? 'border-radius: 23px; padding: 11px 24px 19px 24px;' : ''
  }}

`

const CategoryMobileActiveContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const CategoryMobileArrowImageContainer = styled.div`
${(props: { isOpen: boolean }): string => {
  return props.isOpen ? 'transform: rotate(180deg);' : ''
}}

`

const CategoryMobileActiveText = styled.div`
  font-size: 12px;
  line-height: 18px;
  font-family: 'Good Sans Regular';
  color: ${colors.sand};
  text-transform: uppercase;
`

const CategoryMobileHr = styled.hr`
  height: 1px;
  background-color:${colors.sand};
  border: none;
  margin-top: 8px;
  margin-bottom: 20px;
`

const CategoryMobilePill = styled.div`
  margin-bottom: 7px;
`

const CategoryMobileClearContainer = styled.div`
  cursor: pointer;
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 14px;
`

const CategoryMobileClearText = styled.div`
  text-transform: uppercase;
  color: ${colors.white};
  font-size: 12px;
  line-height: 18px;
  font-family: 'Good Sans Regular';
  text-decoration: underline;
`

const CategoryContainer = styled.div`
  display: flex;
`

const CategoryPill = styled.div`
  font-size: 14px;
  line-height: 21px;
  font-family: 'Good Sans Regular';
  border-radius: 39px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  cursor: pointer;
  ${(props: { name: string, isActive: boolean }): string => {
    return `
    ${props.isActive ? `background-color: ${getColor(props.name)};` : ''}
      border: 1px solid ${getColor(props.name)};
      ${props.isActive ? `padding: 4px 6px 4px 11px;` : `padding: 4px 20px 4px 20px;`}
      &:hover {
        background-color: ${getColor(props.name)};
        border: 1px solid ${getColor(props.name)};
        div {
          color: ${colors.violet};
          opacity: 1;
        }
      }
      `
  }}
}
`
const CategoryPillLink = styled.div`
  font-size: 14px;
  text-decoration: none;

  ${(props: { name: string, isActive: boolean }): string => {
  if (props.isActive) {
    return `color: ${colors.violet};`
  }
  return `color: ${getColor(props.name)};`
}}
`

const XButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background-color: ${colors.violet};
  width: 18px;
  height: 18px;
  margin-left: 5px;
`

function XButtonSvg (props: {name?: string, size?: number}): React.Node {
  const { name, size } = props
  const color = getColor(name)
  return (<svg width={size || 6} height={size || 6} viewBox={`0 0 ${size || 6} ${size || 6}`} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.00586 6.86877L6.49949 1.13098" stroke={color || colors.violet} strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.5 6.86853L1.00637 1.13073" stroke={color || colors.violet} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>)
}

function getColor (name?: string): string {
  switch (name) {
    case 'sex':
      return colors.orange
    case 'wellness':
      return colors.creamsicle
    case 'relationships':
      return colors.pink
    case 'culture':
      return colors.sand
    case 'Dipsea':
      return colors.white
    default:
      return colors.white
  }
}

export default withRouter(Categories)
